import { endOfYear } from 'date-fns/endOfYear';
import { getMonth } from 'date-fns/getMonth';
import { getYear } from 'date-fns/getYear';
import { isSameDay } from 'date-fns/isSameDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfQuarter } from 'date-fns/startOfQuarter';
import { startOfYear } from 'date-fns/startOfYear';
import { sub } from 'date-fns/sub';
import type { BdDateIntervalUnit } from './units';

export class BdDateInterval {
  public static equals(a: BdDateInterval, b: BdDateInterval): boolean {
    return (
      (!a && !b) ||
      (!!a && !!b && isSameDay(a.start, b.start) && isSameDay(a.end, b.end))
    );
  }

  public static byUnit(
    unit: BdDateIntervalUnit,
    referenceDate = new Date(),
  ): BdDateInterval {
    let start, end, referenceMonth, referenceYear, previousYearDate;

    switch (unit) {
      case 'previous-month':
        end = sub(startOfMonth(referenceDate), { days: 1 });
        start = startOfMonth(end);
        break;
      case 'previous-quarter':
        end = sub(startOfQuarter(referenceDate), { days: 1 });
        start = startOfQuarter(end);
        break;
      case 'previous-half-year':
        referenceMonth = getMonth(referenceDate);
        referenceYear = getYear(referenceDate);

        if (referenceMonth > 6) {
          start = new Date(referenceYear, 0, 1);
          end = new Date(referenceYear, 5, 30);
        } else {
          start = new Date(referenceYear - 1, 6, 1);
          end = new Date(referenceYear - 1, 11, 31);
        }

        break;
      case 'previous-year':
      default:
        previousYearDate = sub(referenceDate, { years: 1 });

        start = startOfYear(previousYearDate);
        end = endOfYear(previousYearDate);
        break;
    }

    return new BdDateInterval(start, end);
  }

  public static fromObject(obj: unknown): BdDateInterval {
    const start = new Date(obj['start']);
    const end = new Date(obj['end']);

    return new BdDateInterval(start, end);
  }

  public error: BdDateIntervalErrorConfig = {
    invalidRange: false,
  };

  constructor(
    public start?: Date,
    public end?: Date,
  ) {}

  public isValid(): boolean {
    // check for values exist
    // check for end after or equal to start
    if (this.start && this.end) {
      if (this.end.getTime() >= this.start.getTime()) {
        this.error.invalidRange = false;

        return true;
      } else {
        this.error.invalidRange = true;
      }
    }

    return false;
  }

  public toString(): string {
    return `${this.start.toISOString()} - ${this.end.toISOString()}`;
  }

  public clone(): BdDateInterval {
    return new BdDateInterval(
      new Date(this.start.getTime()),
      new Date(this.end.getTime()),
    );
  }

  public toCompactDateString(locales: string = 'de-DE'): string {
    if (!this.start || !this.end) {
      return '';
    }

    const formatter = new Intl.DateTimeFormat(locales, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });

    if (
      this.start.getFullYear() === this.end.getFullYear() &&
      this.start.getMonth() === 0 &&
      this.start.getDate() === 1 &&
      this.end.getMonth() === 11 &&
      this.end.getDate() === 31
    ) {
      return this.start.getFullYear().toString();
    } else {
      return `${formatter.format(this.start)} - ${formatter.format(this.end)}`;
    }
  }

  public getYears(): number[] {
    const fromYear = this.start.getFullYear();
    const toYear = this.end.getFullYear();
    const years = [];

    for (let currentYear = fromYear; currentYear <= toYear; currentYear++) {
      years.push(currentYear);
    }

    return years;
  }
}

export interface BdDateIntervalErrorConfig {
  invalidRange: boolean;
}
